const SAMPLE_RATE = 44100;

type EnvelopeFunction = (progress: number, time: number) => number;
type WaveType = 'sine' | 'square' | 'sawtooth';

interface FilterOptions {
  type: 'bandpass' | 'lowpass';
  frequency: number | ((time: number, progress: number) => number);
  qualityFactor?: number;
}

function synthNoise(duration: number, envelopeFunction: EnvelopeFunction, filter?: FilterOptions): Float32Array {
  const sampleCount = Math.ceil(SAMPLE_RATE * duration);
  const samples = new Float32Array(sampleCount);

  // Generate white noise
  for (let i = 0; i < sampleCount; i++) {
    samples[i] = Math.random() * 2 - 1;
  }

  // Apply biquad filter if specified
  if (filter) {
    const { type, frequency, qualityFactor = 1 } = filter;
    const varyingFreq = typeof frequency === 'function';

    // Pre-calculate coefficients once for a static frequency
    let fb0 = 0,
      fb1 = 0,
      fb2 = 0,
      ff0 = 0,
      ff1 = 0,
      ff2 = 0;

    // oxlint-disable-next-line no-inner-declarations
    function calcCoefficients(freq: number): void {
      const w = (2 * Math.PI * freq) / SAMPLE_RATE;
      const cosW = Math.cos(w);
      const sinW = Math.sin(w);
      const alpha = sinW / (2 * qualityFactor);
      fb0 = 1 + alpha;
      fb1 = -2 * cosW;
      fb2 = 1 - alpha;
      if (type === 'bandpass') {
        ff0 = alpha;
        ff1 = 0;
        ff2 = -alpha;
      } else {
        // Lowpass
        ff0 = (1 - cosW) / 2;
        ff1 = 1 - cosW;
        ff2 = (1 - cosW) / 2;
      }
    }

    if (!varyingFreq) {
      calcCoefficients(frequency);
    }

    // Filter state variables (previous samples)
    let inputDelay1 = 0;
    let inputDelay2 = 0;
    let outputDelay1 = 0;
    let outputDelay2 = 0;

    // Apply biquad filter
    for (let i = 0; i < sampleCount; i++) {
      if (varyingFreq) {
        calcCoefficients(frequency(i / SAMPLE_RATE, i / sampleCount));
      }

      const currentInput = samples[i] ?? 0;
      const currentOutput =
        (ff0 / fb0) * currentInput + (ff1 / fb0) * inputDelay1 + (ff2 / fb0) * inputDelay2 - (fb1 / fb0) * outputDelay1 - (fb2 / fb0) * outputDelay2;

      samples[i] = currentOutput;

      // Update delay line
      inputDelay2 = inputDelay1;
      inputDelay1 = currentInput;
      outputDelay2 = outputDelay1;
      outputDelay1 = currentOutput;
    }
  }

  // Apply envelope
  for (let i = 0; i < sampleCount; i++) {
    const progress = i / sampleCount;
    const time = i / SAMPLE_RATE;
    samples[i] = (samples[i] ?? 0) * envelopeFunction(progress, time);
  }

  return samples;
}

function synthTone({
  duration,
  frequencyFunction,
  type = 'sine',
  envelopeFunction,
}: {
  duration: number;
  frequencyFunction: ((time: number, progress: number) => number) | number;
  type?: WaveType;
  envelopeFunction?: EnvelopeFunction;
}): Float32Array {
  const sampleCount = Math.ceil(SAMPLE_RATE * duration);
  const output = new Float32Array(sampleCount);
  let phase = 0;

  for (let i = 0; i < sampleCount; i++) {
    const time = i / SAMPLE_RATE;
    const progress = i / sampleCount;
    const frequency = typeof frequencyFunction === 'function' ? frequencyFunction(time, progress) : frequencyFunction;

    // Accumulate phase
    phase += (2 * Math.PI * frequency) / SAMPLE_RATE;

    // Generate waveform
    let sample: number;
    if (type === 'square') {
      sample = Math.sign(Math.sin(phase));
    } else if (type === 'sawtooth') {
      sample = (phase % (2 * Math.PI)) / Math.PI - 1;
    } else {
      // Sine wave
      sample = Math.sin(phase);
    }

    // Apply envelope if provided
    output[i] = sample * (envelopeFunction ? envelopeFunction(progress, time) : 1);
  }

  return output;
}

function mix(...audioArrays: Float32Array[]): Float32Array {
  const maxLength = Math.max(...audioArrays.map((array) => array.length));
  const output = new Float32Array(maxLength);

  // Sum all arrays
  for (const array of audioArrays) {
    for (let i = 0; i < array.length; i++) {
      output[i] = (output[i] ?? 0) + (array[i] ?? 0);
    }
  }

  // Normalize to prevent clipping
  let peakAmplitude = 0;
  for (let i = 0; i < output.length; i++) {
    peakAmplitude = Math.max(peakAmplitude, Math.abs(output[i] ?? 0));
  }

  if (peakAmplitude > 0.95) {
    const normalizationScale = 0.95 / peakAmplitude;
    for (let i = 0; i < output.length; i++) {
      output[i] = (output[i] ?? 0) * normalizationScale;
    }
  }

  return output;
}

function offsetSamples(samples: Float32Array, offsetAmount: number): Float32Array {
  const output = new Float32Array(samples.length + offsetAmount);
  output.set(samples, offsetAmount);
  return output;
}

export const sfxDefinitions = {
  ui_click: (): Float32Array => synthNoise(0.04, (progress) => Math.exp(-progress * 20), { type: 'bandpass', frequency: 1800, qualityFactor: 0.5 }),

  // Warmer, lower frequency — less harsh than the default
  ui_click_soft_wood: (): Float32Array =>
    synthNoise(0.05, (progress) => Math.exp(-progress * 22), { type: 'bandpass', frequency: 900, qualityFactor: 0.7 }),

  // Clean tonal click with a subtle pitch drop, no noise
  ui_click_sine_pop: (): Float32Array =>
    synthTone({
      duration: 0.04,
      frequencyFunction: (time: number) => 800 * Math.exp(-time * 20),
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 150) * 0.8,
    }),

  // Higher pitched, bright pop
  ui_click_sine_pop_bright: (): Float32Array =>
    synthTone({
      duration: 0.035,
      frequencyFunction: (time: number) => 1400 * Math.exp(-time * 25),
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 180) * 0.75,
    }),

  // Lower, warmer pop
  ui_click_sine_pop_warm: (): Float32Array =>
    synthTone({
      duration: 0.05,
      frequencyFunction: (time: number) => 500 * Math.exp(-time * 15),
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 120) * 0.8,
    }),

  // Quick, sharp pop with fast decay
  ui_click_sine_pop_sharp: (): Float32Array =>
    synthTone({
      duration: 0.03,
      frequencyFunction: (time: number) => 1000 * Math.exp(-time * 30),
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 200) * 0.85,
    }),

  // Gentle, soft pop with slower decay
  ui_click_sine_pop_soft: (): Float32Array =>
    synthTone({
      duration: 0.06,
      frequencyFunction: (time: number) => 650 * Math.exp(-time * 12),
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 100) * 0.7,
    }),

  // Deep bass pop
  ui_click_sine_pop_bass: (): Float32Array =>
    synthTone({
      duration: 0.055,
      frequencyFunction: (time: number) => 300 * Math.exp(-time * 10),
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 90) * 0.85,
    }),

  // Noise burst + pitched transient — like a physical button
  ui_click_mechanical: (): Float32Array => {
    const noise = synthNoise(0.03, (progress) => Math.exp(-progress * 40), { type: 'bandpass', frequency: 2200, qualityFactor: 0.4 });
    const tone = synthTone({
      duration: 0.035,
      frequencyFunction: (time: number) => 500 - time * 3000,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 50) * 0.4,
    });
    return mix(noise, tone);
  },

  // Low, punchy — almost game-controller feel
  ui_click_thunk_high: (): Float32Array => {
    const noise = synthNoise(0.06, (progress) => Math.exp(-progress * 7), { type: 'lowpass', frequency: 300, qualityFactor: 0.1 });
    const tone = synthTone({
      duration: 0.05,
      frequencyFunction: (time: number) => 180 - time * 600,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 20) * 0.6,
    });
    return mix(noise, tone);
  },

  // Low, punchy — almost game-controller feel
  ui_click_thunk: (): Float32Array => {
    const noise = synthNoise(0.06, (progress) => Math.exp(-progress * 18), { type: 'lowpass', frequency: 500, qualityFactor: 0.9 });
    const tone = synthTone({
      duration: 0.05,
      frequencyFunction: (time: number) => 180 - time * 800,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 20) * 0.6,
    });
    return mix(noise, tone);
  },

  // Very short, bright, minimal — like a keyboard switch
  ui_click_tick: (): Float32Array =>
    synthNoise(0.022, (progress) => Math.exp(-progress * 55), { type: 'bandpass', frequency: 3200, qualityFactor: 0.35 }),

  // Chat send — short ascending sweep
  chat_send: (): Float32Array =>
    synthTone({
      duration: 0.07,
      frequencyFunction: (time: number) => 240 + time * 2800,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 18) * 0.65,
    }),

  combat_killing_blow: (): Float32Array => {
    const thudSound = synthNoise(0.15, (progress) => Math.exp(-progress * 8) * 0.6, { type: 'lowpass', frequency: 400, qualityFactor: 0.8 });
    const crackSound = synthNoise(0.06, (progress) => Math.exp(-progress * 30), { type: 'bandpass', frequency: 200, qualityFactor: 0.4 });
    const fallingTone = synthTone({
      duration: 0.2,
      frequencyFunction: (time: number) => 120 - time * 60,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 10) * 0.4,
    });
    return mix(thudSound, crackSound, offsetSamples(fallingTone, Math.ceil(SAMPLE_RATE * 0.02)));
  },

  // Killing blow — heavy slam with deep impact
  combat_killing_blow_slam: (): Float32Array => {
    const deepImpact = synthNoise(0.25, (progress) => Math.exp(-progress * 5) * 0.7, { type: 'lowpass', frequency: 300, qualityFactor: 1.2 });
    const subBass = synthTone({
      duration: 0.3,
      frequencyFunction: 60,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 6) * 0.5,
    });
    const crack = synthNoise(0.08, (progress) => Math.exp(-progress * 25), { type: 'bandpass', frequency: 800, qualityFactor: 0.6 });
    return mix(deepImpact, subBass, offsetSamples(crack, Math.ceil(SAMPLE_RATE * 0.01)));
  },

  // Killing blow — sharp slash/cut sound
  combat_killing_blow_slash: (): Float32Array => {
    const swish = synthNoise(
      0.18,
      (progress) => {
        // Quick attack, sustained mid, fast decay
        if (progress < 0.1) {
          return (progress / 0.1) * 0.65;
        }
        return Math.exp(-(progress - 0.1) * 12) * 0.65;
      },
      {
        type: 'bandpass',
        frequency: (time: number) => 2400 - time * 1800,
        qualityFactor: 1.5,
      },
    );
    const cutTone = synthTone({
      duration: 0.12,
      frequencyFunction: (time: number) => 1200 - time * 1000,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 18) * 0.35,
    });
    const impact = synthNoise(0.06, (progress) => Math.exp(-progress * 35) * 0.55, { type: 'lowpass', frequency: 500, qualityFactor: 0.8 });
    return mix(swish, cutTone, offsetSamples(impact, Math.ceil(SAMPLE_RATE * 0.12)));
  },

  // Killing blow — explosive critical hit
  combat_killing_blow_critical: (): Float32Array => {
    const explosion = synthNoise(0.2, (progress) => Math.exp(-progress * 10) * 0.7, { type: 'lowpass', frequency: 600, qualityFactor: 0.9 });
    const boom = synthTone({
      duration: 0.25,
      frequencyFunction: (time: number) => 100 * Math.exp(-time * 8),
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 7) * 0.6,
    });
    const highCrackle = synthNoise(0.12, (progress) => Math.exp(-progress * 20) * 0.45, { type: 'bandpass', frequency: 1800, qualityFactor: 0.5 });
    const risingTone = synthTone({
      duration: 0.15,
      frequencyFunction: (time: number) => 200 + time * 800,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.min(progress * 4, 1) * Math.exp(-progress * 15) * 0.4,
    });
    return mix(explosion, boom, highCrackle, risingTone);
  },

  // Killing blow — crushing impact with rumble
  combat_killing_blow_crush: (): Float32Array => {
    const rumble = synthNoise(
      0.35,
      (progress) => {
        // Sustained rumble that fades
        return Math.exp(-progress * 4) * 0.6;
      },
      { type: 'lowpass', frequency: 250, qualityFactor: 1.5 },
    );
    const impact = synthNoise(0.08, (progress) => Math.exp(-progress * 20) * 0.65, { type: 'lowpass', frequency: 600, qualityFactor: 0.7 });
    const crushTone = synthTone({
      duration: 0.25,
      frequencyFunction: (time: number) => 80 + Math.sin(time * 30) * 40,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 8) * 0.45,
    });
    return mix(rumble, impact, crushTone);
  },

  // Killing blow — quick brutal stab
  combat_killing_blow_stab: (): Float32Array => {
    const pierce = synthNoise(
      0.12,
      (progress) => {
        // Very fast attack
        if (progress < 0.05) {
          return (progress / 0.05) * 0.7;
        }
        return Math.exp(-(progress - 0.05) * 18) * 0.7;
      },
      { type: 'bandpass', frequency: 1600, qualityFactor: 2.0 },
    );
    const impact = synthNoise(0.05, (progress) => Math.exp(-progress * 40) * 0.6, { type: 'lowpass', frequency: 450, qualityFactor: 0.9 });
    const sharpTone = synthTone({
      duration: 0.1,
      frequencyFunction: (time: number) => 1800 - time * 1400,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 22) * 0.3,
    });
    return mix(pierce, sharpTone, offsetSamples(impact, Math.ceil(SAMPLE_RATE * 0.02)));
  },

  // Quick triumphant fanfare
  game_fanfare: (): Float32Array => {
    // C5, E5, G5 chord progression (523, 659, 784)
    const chord1note1 = synthTone({
      duration: 0.18,
      frequencyFunction: 523,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 10) * 0.35,
    });
    const chord1note2 = synthTone({
      duration: 0.18,
      frequencyFunction: 659,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 10) * 0.35,
    });
    const chord2note1 = synthTone({
      duration: 0.25,
      frequencyFunction: 659,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 7) * 0.4,
    });
    const chord2note2 = synthTone({
      duration: 0.25,
      frequencyFunction: 784,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 7) * 0.4,
    });

    const beatLength = Math.ceil(SAMPLE_RATE * 0.12);
    return mix(chord1note1, chord1note2, offsetSamples(chord2note1, beatLength), offsetSamples(chord2note2, beatLength));
  },

  // Short celebratory chime
  game_chime: (): Float32Array => {
    // Quick ascending three-note chime
    const note1 = synthTone({
      duration: 0.12,
      frequencyFunction: 659,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 15) * 0.4,
    });
    const note2 = synthTone({
      duration: 0.12,
      frequencyFunction: 784,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 15) * 0.4,
    });
    const note3 = synthTone({
      duration: 0.2,
      frequencyFunction: 1047,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 10) * 0.45,
    });

    // Add sparkle
    const sparkle = synthTone({
      duration: 0.2,
      frequencyFunction: 2093,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 12) * 0.15,
    });

    const beatLength = Math.ceil(SAMPLE_RATE * 0.07);
    return mix(note1, offsetSamples(note2, beatLength), offsetSamples(note3, beatLength * 2), offsetSamples(sparkle, beatLength * 2));
  },

  // Notification dismiss — noise-based lateral swipe feel
  notification_dismiss: (): Float32Array =>
    synthNoise(0.12, (progress) => (1 - progress) * Math.exp(-progress * 10) * 0.65, { type: 'lowpass', frequency: 700, qualityFactor: 0.7 }),

  // Chat receive — brighter high ping
  chat_receive: (): Float32Array => {
    const tone = synthTone({
      duration: 0.18,
      frequencyFunction: 1320,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 14) * 0.38,
    });
    const harmonic = synthTone({
      duration: 0.14,
      frequencyFunction: 1980,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 18) * 0.15,
    });
    return mix(tone, offsetSamples(harmonic, Math.ceil(SAMPLE_RATE * 0.09)));
  },

  // Mail receive — punchy two-note alert
  mail_receive: (): Float32Array => {
    const tone1 = synthTone({
      duration: 0.14,
      frequencyFunction: 230,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 4) * 0.55,
    });
    const tone2 = synthTone({
      duration: 0.2,
      frequencyFunction: 510,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 9) * 0.55,
    });
    const tone3 = synthTone({
      duration: 0.14,
      frequencyFunction: 900,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 7) * 0.55,
    });
    return mix(tone1, offsetSamples(tone2, Math.ceil(SAMPLE_RATE * 0.1)), offsetSamples(tone3, Math.ceil(SAMPLE_RATE * 0.2)));
  },

  // Mail send — ascending frequency sweep with noise
  mail_send: (): Float32Array => {
    const sweep = synthTone({
      duration: 0.18,
      frequencyFunction: (time: number) => 200 + time * 3200,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.min(progress * 10, 1) * Math.exp(-progress * 9) * 0.62,
    });
    const noise = synthNoise(0.1, (progress) => Math.exp(-progress * 20) * 0.28, { type: 'lowpass', frequency: 1200, qualityFactor: 0.6 });
    return mix(sweep, noise);
  },

  bubbles: (): Float32Array => {
    const riseTone = synthTone({
      duration: 0.45,
      frequencyFunction: (time: number) => 110 + time * 99,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.min(progress * 8, 1) * Math.exp(-progress * 6) * 0.26,
    });
    const riseTone2 = synthTone({
      duration: 0.45,
      frequencyFunction: (time: number) => 110 + time * 999,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.min(progress * 8, 1) * Math.exp(-progress * 6) * 0.26,
    });
    const riseTone3 = synthTone({
      duration: 0.45,
      frequencyFunction: (time: number) => 110 + time * 1999,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.min(progress * 8, 1) * Math.exp(-progress * 6) * 0.26,
    });

    const beat = Math.ceil(SAMPLE_RATE * 0.12); // 120ms grid

    return mix(
      riseTone, // starts at 0ms
      offsetSamples(riseTone2, Math.round(beat * 3.5)), // starts at ~420ms (3.5 * 120ms)
      offsetSamples(riseTone3, beat * 6), // starts at ~720ms (6 * 120ms)
    );
  },

  drag_start: (): Float32Array => {
    const tone = synthTone({
      duration: 0.2,
      frequencyFunction: (time: number) => 280 + time * 2900,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.min(progress * 5, 1) * Math.exp(-progress * 11) * 0.28,
    });
    return mix(tone);
  },

  drag_end: (): Float32Array => {
    const tone = synthTone({
      duration: 0.2,
      frequencyFunction: (time: number) => 280 + time * 580,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.min(progress * 5, 1) * Math.exp(-progress * 11) * 0.18,
    });
    return mix(tone);
  },

  move_item: (): Float32Array => {
    const tone = synthTone({
      duration: 0.2,
      frequencyFunction: (time: number) => 380 + time * 280,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.min(progress * 5, 1) * Math.exp(-progress * 11) * 0.18,
    });
    return mix(tone);
  },

  modal_open: (): Float32Array => {
    const tone = synthTone({
      duration: 0.2,
      frequencyFunction: (time: number) => 280 + time * 2900,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.min(progress * 5, 1) * Math.exp(-progress * 11) * 0.28,
    });
    return mix(tone);
  },

  // Modal close — gentle falling tone with harmonic
  modal_close: (): Float32Array => {
    const tone = synthTone({
      duration: 0.17,
      frequencyFunction: (time: number) => 460 * Math.exp(-time * 9),
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 12) * 0.38,
    });
    const harmonic = synthTone({
      duration: 0.14,
      frequencyFunction: (time: number) => 690 * Math.exp(-time * 11),
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 15) * 0.17,
    });
    return mix(tone, harmonic);
  },

  // Rustle searching — burst-based, like shuffling through items
  game_search_arena: (): Float32Array => {
    const burst1 = synthNoise(0.15, (progress) => Math.exp(-progress * 8) * 0.65, { type: 'bandpass', frequency: 600, qualityFactor: 1.4 });
    const burst2 = synthNoise(0.16, (progress) => Math.exp(-progress * 7) * 0.6, { type: 'bandpass', frequency: 500, qualityFactor: 1.6 });
    const burst3 = synthNoise(0.14, (progress) => Math.exp(-progress * 9) * 0.7, { type: 'bandpass', frequency: 700, qualityFactor: 1.3 });
    const burst4 = synthNoise(0.15, (progress) => Math.exp(-progress * 7.5) * 0.55, { type: 'bandpass', frequency: 550, qualityFactor: 1.5 });

    return mix(
      burst1,
      offsetSamples(burst2, Math.ceil(SAMPLE_RATE * 0.16)),
      offsetSamples(burst3, Math.ceil(SAMPLE_RATE * 0.33)),
      offsetSamples(burst4, Math.ceil(SAMPLE_RATE * 0.5)),
    );
  },

  // Rustling — quick snappy bursts
  rustle_snappy: (): Float32Array => {
    const burst1 = synthNoise(0.06, (progress) => Math.exp(-progress * 20) * 0.7, { type: 'bandpass', frequency: 1500, qualityFactor: 1.0 });
    const burst2 = synthNoise(0.05, (progress) => Math.exp(-progress * 25) * 0.65, { type: 'bandpass', frequency: 1100, qualityFactor: 1.2 });
    const burst3 = synthNoise(0.06, (progress) => Math.exp(-progress * 18) * 0.7, { type: 'bandpass', frequency: 1300, qualityFactor: 0.9 });

    return mix(burst1, offsetSamples(burst2, Math.ceil(SAMPLE_RATE * 0.07)), offsetSamples(burst3, Math.ceil(SAMPLE_RATE * 0.13)));
  },

  // Rustling — paper crinkle bursts
  rustle_paper: (): Float32Array => {
    const burst1 = synthNoise(0.07, (progress) => Math.exp(-progress * 16) * 0.6, { type: 'bandpass', frequency: 1800, qualityFactor: 1.5 });
    const burst2 = synthNoise(0.06, (progress) => Math.exp(-progress * 18) * 0.55, { type: 'bandpass', frequency: 1600, qualityFactor: 1.6 });
    const burst3 = synthNoise(0.08, (progress) => Math.exp(-progress * 14) * 0.65, { type: 'bandpass', frequency: 1900, qualityFactor: 1.4 });
    const burst4 = synthNoise(0.05, (progress) => Math.exp(-progress * 22) * 0.5, { type: 'bandpass', frequency: 1700, qualityFactor: 1.7 });

    return mix(
      burst1,
      offsetSamples(burst2, Math.ceil(SAMPLE_RATE * 0.08)),
      offsetSamples(burst3, Math.ceil(SAMPLE_RATE * 0.15)),
      offsetSamples(burst4, Math.ceil(SAMPLE_RATE * 0.24)),
    );
  },

  game_craft_item: (): Float32Array => {
    // Metallic "tink" sounds - three distinct taps
    const tink1 = synthTone({
      duration: 0.08,
      frequencyFunction: (time: number) => 1800 - time * 400,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 35) * 0.5,
    });
    const tink1harmonic = synthTone({
      duration: 0.06,
      frequencyFunction: (time: number) => 3600 - time * 800,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 45) * 0.25,
    });
    const tink1noise = synthNoise(0.035, (progress) => Math.exp(-progress * 50) * 0.3, { type: 'bandpass', frequency: 2200, qualityFactor: 0.8 });

    const tink2 = synthTone({
      duration: 0.08,
      frequencyFunction: (time: number) => 1900 - time * 450,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 35) * 0.5,
    });
    const tink2harmonic = synthTone({
      duration: 0.06,
      frequencyFunction: (time: number) => 3800 - time * 900,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 45) * 0.25,
    });
    const tink2noise = synthNoise(0.035, (progress) => Math.exp(-progress * 50) * 0.3, { type: 'bandpass', frequency: 2300, qualityFactor: 0.8 });

    const tink3 = synthTone({
      duration: 0.08,
      frequencyFunction: (time: number) => 2000 - time * 400,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 35) * 0.5,
    });
    const tink3harmonic = synthTone({
      duration: 0.06,
      frequencyFunction: (time: number) => 4000 - time * 800,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 45) * 0.25,
    });
    const tink3noise = synthNoise(0.035, (progress) => Math.exp(-progress * 50) * 0.3, { type: 'bandpass', frequency: 2400, qualityFactor: 0.8 });

    const tinkSpacing = Math.ceil(SAMPLE_RATE * 0.12); // 120ms between tinks

    return mix(
      tink1,
      tink1harmonic,
      tink1noise,
      offsetSamples(tink2, tinkSpacing),
      offsetSamples(tink2harmonic, tinkSpacing),
      offsetSamples(tink2noise, tinkSpacing),
      offsetSamples(tink3, tinkSpacing * 2),
      offsetSamples(tink3harmonic, tinkSpacing * 2),
      offsetSamples(tink3noise, tinkSpacing * 2),
    );
  },

  // Rustling — light quick pocket search
  game_equip_item: (): Float32Array => {
    const burst1 = synthNoise(0.05, (progress) => Math.exp(-progress * 22) * 0.6, { type: 'bandpass', frequency: 1600, qualityFactor: 1.1 });
    const burst2 = synthNoise(0.04, (progress) => Math.exp(-progress * 25) * 0.55, { type: 'bandpass', frequency: 1700, qualityFactor: 1.0 });
    const burst3 = synthNoise(0.05, (progress) => Math.exp(-progress * 20) * 0.6, { type: 'bandpass', frequency: 1550, qualityFactor: 1.2 });

    return mix(burst1, offsetSamples(burst2, Math.ceil(SAMPLE_RATE * 0.06)), offsetSamples(burst3, Math.ceil(SAMPLE_RATE * 0.11)));
  },

  // Rustling — light quick pocket search
  game_unequip_item: (): Float32Array => {
    const burst1 = synthNoise(0.05, (progress) => Math.exp(-progress * 22) * 0.6, { type: 'bandpass', frequency: 1550, qualityFactor: 1.1 });
    const burst2 = synthNoise(0.04, (progress) => Math.exp(-progress * 25) * 0.55, { type: 'bandpass', frequency: 1700, qualityFactor: 1.0 });
    const burst3 = synthNoise(0.05, (progress) => Math.exp(-progress * 20) * 0.6, { type: 'bandpass', frequency: 1100, qualityFactor: 1.2 });

    return mix(burst1, offsetSamples(burst2, Math.ceil(SAMPLE_RATE * 0.09)), offsetSamples(burst3, Math.ceil(SAMPLE_RATE * 0.11)));
  },

  // Rustling — fabric movement bursts
  rustle_fabric: (): Float32Array => {
    const burst1 = synthNoise(0.08, (progress) => Math.exp(-progress * 14) * 0.6, { type: 'bandpass', frequency: 1100, qualityFactor: 1.4 });
    const burst2 = synthNoise(0.07, (progress) => Math.exp(-progress * 16) * 0.55, { type: 'bandpass', frequency: 1250, qualityFactor: 1.3 });
    const burst3 = synthNoise(0.09, (progress) => Math.exp(-progress * 13) * 0.65, { type: 'bandpass', frequency: 1050, qualityFactor: 1.5 });
    const burst4 = synthNoise(0.06, (progress) => Math.exp(-progress * 18) * 0.5, { type: 'bandpass', frequency: 1200, qualityFactor: 1.35 });

    return mix(
      burst1,
      offsetSamples(burst2, Math.ceil(SAMPLE_RATE * 0.09)),
      offsetSamples(burst3, Math.ceil(SAMPLE_RATE * 0.17)),
      offsetSamples(burst4, Math.ceil(SAMPLE_RATE * 0.27)),
    );
  },

  game_use_item: (): Float32Array => {
    const burst1 = synthNoise(0.07, (progress) => Math.exp(-progress * 15) * 0.58, { type: 'bandpass', frequency: 1350, qualityFactor: 1.3 });
    const burst2 = synthNoise(0.08, (progress) => Math.exp(-progress * 13) * 0.62, { type: 'bandpass', frequency: 1450, qualityFactor: 1.2 });
    const burst3 = synthNoise(0.06, (progress) => Math.exp(-progress * 17) * 0.55, { type: 'bandpass', frequency: 1300, qualityFactor: 1.4 });
    const burst4 = synthNoise(0.09, (progress) => Math.exp(-progress * 12) * 0.6, { type: 'bandpass', frequency: 1400, qualityFactor: 1.25 });
    const burst5 = synthNoise(0.07, (progress) => Math.exp(-progress * 16) * 0.57, { type: 'bandpass', frequency: 1500, qualityFactor: 1.35 });

    return mix(
      burst1,
      offsetSamples(burst2, Math.ceil(SAMPLE_RATE * 0.08)),
      offsetSamples(burst3, Math.ceil(SAMPLE_RATE * 0.16)),
      offsetSamples(burst4, Math.ceil(SAMPLE_RATE * 0.23)),
      offsetSamples(burst5, Math.ceil(SAMPLE_RATE * 0.32)),
    );
  },

  combat_button_click: (): Float32Array =>
    synthNoise(
      0.25,
      (progress) => {
        const wave = Math.sin(progress * Math.PI * 8) * 0.35 + 0.65;
        return wave * Math.exp(-progress * 8) * 0.5;
      },
      {
        type: 'bandpass',
        frequency: (time: number) => 1800 + Math.sin(time * 30) * 700,
        qualityFactor: 1.2,
      },
    ),

  // Hospital healing — restorative ascending tones with sparkle
  game_hospital_heal: (): Float32Array => {
    // High sparkle notes
    const sparkle1 = synthTone({
      duration: 0.2,
      frequencyFunction: 1320,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 12) * 0.25,
    });

    const sparkle2 = synthTone({
      duration: 0.22,
      frequencyFunction: 1760,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 10) * 0.22,
    });

    const sparkle3 = synthTone({
      duration: 0.25,
      frequencyFunction: 2093,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 8) * 0.2,
    });

    // Soft shimmer noise
    const shimmer = synthNoise(
      0.45,
      (progress) => {
        if (progress < 0.2) {
          return (progress / 0.2) * Math.exp(-progress * 4) * 0.15;
        }
        return Math.exp(-progress * 6) * 0.15;
      },
      { type: 'bandpass', frequency: 3200, qualityFactor: 2.5 },
    );

    return mix(
      offsetSamples(sparkle1, Math.ceil(SAMPLE_RATE * 0.1)),
      offsetSamples(sparkle2, Math.ceil(SAMPLE_RATE * 0.18)),
      offsetSamples(sparkle3, Math.ceil(SAMPLE_RATE * 0.25)),
      shimmer,
    );
  },

  // Consumable use — liquid potion gulp (three distinct gulp sounds)
  game_use_consumable_potion: (): Float32Array => {
    const gulp1 = synthTone({
      duration: 0.08,
      frequencyFunction: (time: number) => 180 - time * 90,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.sin(progress * Math.PI) * 0.45,
    });
    const gulp2 = synthTone({
      duration: 0.09,
      frequencyFunction: (time: number) => 170 - time * 85,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.sin(progress * Math.PI) * 0.48,
    });
    const gulp3 = synthTone({
      duration: 0.1,
      frequencyFunction: (time: number) => 160 - time * 80,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.sin(progress * Math.PI) * 0.5,
    });
    const liquid = synthNoise(0.3, (progress) => Math.sin(progress * Math.PI * 3) * 0.2, { type: 'lowpass', frequency: 400, qualityFactor: 1.2 });

    return mix(gulp1, offsetSamples(gulp2, Math.ceil(SAMPLE_RATE * 0.1)), offsetSamples(gulp3, Math.ceil(SAMPLE_RATE * 0.2)), liquid);
  },

  // Consumable use — magical tonic with sparkle
  game_use_consumable_tonic: (): Float32Array => {
    const rising = synthTone({
      duration: 0.25,
      frequencyFunction: (time: number) => 440 + time * 880,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.min(progress * 3, 1) * Math.exp(-progress * 6) * 0.4,
    });
    const sparkle1 = synthTone({
      duration: 0.15,
      frequencyFunction: 1320,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 12) * 0.25,
    });
    const sparkle2 = synthTone({
      duration: 0.18,
      frequencyFunction: 1760,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 10) * 0.22,
    });
    const shimmer = synthNoise(0.28, (progress) => Math.exp(-progress * 8) * 0.15, { type: 'bandpass', frequency: 2800, qualityFactor: 2.0 });

    return mix(rising, offsetSamples(sparkle1, Math.ceil(SAMPLE_RATE * 0.12)), offsetSamples(sparkle2, Math.ceil(SAMPLE_RATE * 0.18)), shimmer);
  },

  // Consumable use — quick fizz/pop (like opening a bottle)
  game_use_consumable_fizz: (): Float32Array => {
    const pop = synthTone({
      duration: 0.05,
      frequencyFunction: (time: number) => 800 - time * 600,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 40) * 0.55,
    });
    const fizz = synthNoise(0.25, (progress) => Math.exp(-progress * 10) * 0.35, { type: 'bandpass', frequency: 4000, qualityFactor: 1.0 });
    const bubble = synthTone({
      duration: 0.2,
      frequencyFunction: (time: number) => 300 + Math.sin(time * 80) * 100,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 8) * 0.25,
    });

    return mix(pop, offsetSamples(fizz, Math.ceil(SAMPLE_RATE * 0.02)), offsetSamples(bubble, Math.ceil(SAMPLE_RATE * 0.05)));
  },

  // Consumable use — energetic whoosh (energy drink style)
  game_use_consumable_energy: (): Float32Array => {
    const whoosh = synthNoise(0.22, (progress) => Math.sin(progress * Math.PI) * 0.5, {
      type: 'bandpass',
      frequency: (time: number) => 800 + time * 2400,
      qualityFactor: 1.5,
    });
    const zap = synthTone({
      duration: 0.18,
      frequencyFunction: (time: number) => 220 + time * 1800,
      type: 'square',
      envelopeFunction: (progress: number) => Math.min(progress * 4, 1) * Math.exp(-progress * 10) * 0.3,
    });

    return mix(whoosh, zap);
  },

  // Consumable use — mystical magical effect
  game_use_consumable_mystical: (): Float32Array => {
    const tone1 = synthTone({
      duration: 0.2,
      frequencyFunction: 523,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 8) * 0.35,
    });
    const tone2 = synthTone({
      duration: 0.22,
      frequencyFunction: 659,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 7) * 0.32,
    });
    const tone3 = synthTone({
      duration: 0.25,
      frequencyFunction: 784,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 6) * 0.3,
    });
    const warble = synthTone({
      duration: 0.3,
      frequencyFunction: (time: number) => 1568 + Math.sin(time * 40) * 200,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 7) * 0.2,
    });

    return mix(tone1, offsetSamples(tone2, Math.ceil(SAMPLE_RATE * 0.05)), offsetSamples(tone3, Math.ceil(SAMPLE_RATE * 0.1)), warble);
  },

  game_use_consumable_crunch: (): Float32Array => {
    const crunch1 = synthNoise(0.08, (progress) => Math.exp(-progress * 15) * 0.6, { type: 'bandpass', frequency: 1200, qualityFactor: 0.8 });
    const crunch2 = synthNoise(0.09, (progress) => Math.exp(-progress * 13) * 0.62, { type: 'bandpass', frequency: 1350, qualityFactor: 0.7 });
    const crunch3 = synthNoise(0.07, (progress) => Math.exp(-progress * 17) * 0.58, { type: 'bandpass', frequency: 1100, qualityFactor: 0.9 });
    const lowRumble = synthTone({
      duration: 0.3,
      frequencyFunction: 120,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 8) * 0.25,
    });

    return mix(crunch1, offsetSamples(crunch2, Math.ceil(SAMPLE_RATE * 0.1)), offsetSamples(crunch3, Math.ceil(SAMPLE_RATE * 0.2)), lowRumble);
  },

  game_use_consumable_gulp: (): Float32Array => {
    const gulp = synthTone({
      duration: 0.12,
      frequencyFunction: (time: number) => 200 - time * 100,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.sin(progress * Math.PI) * 0.5,
    });
    const swallow = synthNoise(
      0.15,
      (progress) => {
        if (progress < 0.3) {
          return (progress / 0.3) * 0.25;
        }
        return Math.exp(-(progress - 0.3) * 8) * 0.25;
      },
      { type: 'lowpass', frequency: 350, qualityFactor: 1.0 },
    );

    return mix(gulp, swallow);
  },

  game_use_consumable_restore: (): Float32Array => {
    const note1 = synthTone({
      duration: 0.15,
      frequencyFunction: 440,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 10) * 0.4,
    });
    const note2 = synthTone({
      duration: 0.16,
      frequencyFunction: 554,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 9) * 0.38,
    });
    const note3 = synthTone({
      duration: 0.18,
      frequencyFunction: 659,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 8) * 0.36,
    });
    const harmonic = synthTone({
      duration: 0.25,
      frequencyFunction: 1318,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 10) * 0.15,
    });

    return mix(
      note1,
      offsetSamples(note2, Math.ceil(SAMPLE_RATE * 0.08)),
      offsetSamples(note3, Math.ceil(SAMPLE_RATE * 0.16)),
      offsetSamples(harmonic, Math.ceil(SAMPLE_RATE * 0.16)),
    );
  },

  game_use_consumable_bubble: (): Float32Array => {
    const bubbles = synthNoise(0.35, (progress) => Math.sin(progress * Math.PI * 6) * Math.exp(-progress * 4) * 0.4, {
      type: 'bandpass',
      frequency: (time: number) => 800 + Math.sin(time * 50) * 400,
      qualityFactor: 1.8,
    });
    const popTone = synthTone({
      duration: 0.3,
      frequencyFunction: (time: number) => 400 + Math.sin(time * 25) * 200,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 6) * 0.25,
    });

    return mix(bubbles, popTone);
  },

  game_perform_job: (): Float32Array => {
    const hum = synthTone({
      duration: 0.35,
      frequencyFunction: 220,
      type: 'sine',
      envelopeFunction: (progress: number) => {
        if (progress < 0.15) {
          return (progress / 0.15) * 0.4;
        }
        return Math.exp(-(progress - 0.15) * 6) * 0.4;
      },
    });
    const chime = synthTone({
      duration: 0.25,
      frequencyFunction: 880,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 8) * 0.25,
    });
    const highChime = synthTone({
      duration: 0.2,
      frequencyFunction: 1760,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 10) * 0.18,
    });

    return mix(hum, offsetSamples(chime, Math.ceil(SAMPLE_RATE * 1)), offsetSamples(highChime, Math.ceil(SAMPLE_RATE * 0.15)));
  },

  game_gain_item_soft: (): Float32Array => {
    const chime = synthTone({
      duration: 0.3,
      frequencyFunction: 880,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 6) * 0.28,
    });
    const harmonic = synthTone({
      duration: 0.25,
      frequencyFunction: 1320,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 8) * 0.15,
    });
    return mix(chime, harmonic);
  },

  game_gain_item_bell: (): Float32Array => {
    const bell = synthTone({
      duration: 0.35,
      frequencyFunction: 659,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 5) * 0.3,
    });
    const overtone1 = synthTone({
      duration: 0.3,
      frequencyFunction: 1318,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 7) * 0.12,
    });
    const overtone2 = synthTone({
      duration: 0.25,
      frequencyFunction: 1976,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 9) * 0.08,
    });
    return mix(bell, overtone1, overtone2);
  },

  game_gain_item_sparkle: (): Float32Array => {
    const sparkle1 = synthTone({
      duration: 0.2,
      frequencyFunction: 1047,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 10) * 0.25,
    });
    const sparkle2 = synthTone({
      duration: 0.22,
      frequencyFunction: 1397,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 9) * 0.22,
    });
    const shimmer = synthNoise(0.25, (progress) => Math.exp(-progress * 12) * 0.1, { type: 'bandpass', frequency: 2500, qualityFactor: 2.5 });
    return mix(sparkle1, offsetSamples(sparkle2, Math.ceil(SAMPLE_RATE * 0.05)), shimmer);
  },

  game_gain_item_warm: (): Float32Array => {
    const rise = synthTone({
      duration: 0.25,
      frequencyFunction: (time: number) => 440 + time * 440,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.min(progress * 4, 1) * Math.exp(-progress * 7) * 0.3,
    });
    const sub = synthTone({
      duration: 0.3,
      frequencyFunction: 220,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 5) * 0.2,
    });
    return mix(rise, sub);
  },

  game_gain_money_clink: (): Float32Array => {
    const clink = synthTone({
      duration: 0.08,
      frequencyFunction: (time: number) => 2200 - time * 300,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 25) * 0.3,
    });
    const metallic = synthNoise(0.06, (progress) => Math.exp(-progress * 30) * 0.18, { type: 'bandpass', frequency: 2500, qualityFactor: 1.2 });
    return mix(clink, metallic);
  },

  game_gain_money_chime: (): Float32Array => {
    const tone1 = synthTone({
      duration: 0.2,
      frequencyFunction: 523,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 8) * 0.28,
    });
    const tone2 = synthTone({
      duration: 0.25,
      frequencyFunction: 784,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 7) * 0.26,
    });
    return mix(tone1, offsetSamples(tone2, Math.ceil(SAMPLE_RATE * 0.06)));
  },

  game_gain_money_register: (): Float32Array => {
    const ding = synthTone({
      duration: 0.15,
      frequencyFunction: 1760,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 12) * 0.32,
    });
    const echo = synthTone({
      duration: 0.18,
      frequencyFunction: 1760,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 10) * 0.15,
    });
    return mix(ding, offsetSamples(echo, Math.ceil(SAMPLE_RATE * 0.05)));
  },

  game_gain_money_jingle: (): Float32Array => {
    const note1 = synthTone({
      duration: 0.12,
      frequencyFunction: 659,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 14) * 0.26,
    });
    const note2 = synthTone({
      duration: 0.12,
      frequencyFunction: 880,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 14) * 0.24,
    });
    const note3 = synthTone({
      duration: 0.15,
      frequencyFunction: 1047,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 12) * 0.28,
    });
    return mix(note1, offsetSamples(note2, Math.ceil(SAMPLE_RATE * 0.05)), offsetSamples(note3, Math.ceil(SAMPLE_RATE * 0.1)));
  },

  game_gain_item_windchime: (): Float32Array => {
    const chime1 = synthTone({
      duration: 0.25,
      frequencyFunction: 987,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 6) * 0.22,
    });
    const chime2 = synthTone({
      duration: 0.28,
      frequencyFunction: 1318,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 5.5) * 0.2,
    });
    const chime3 = synthTone({
      duration: 0.3,
      frequencyFunction: 1568,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 5) * 0.18,
    });
    const air = synthNoise(0.35, (progress) => Math.exp(-progress * 6) * 0.08, { type: 'bandpass', frequency: 3500, qualityFactor: 3.0 });
    return mix(chime1, offsetSamples(chime2, Math.ceil(SAMPLE_RATE * 0.08)), offsetSamples(chime3, Math.ceil(SAMPLE_RATE * 0.15)), air);
  },

  coin_clink_single: (): Float32Array => {
    const mainTone = synthTone({
      duration: 0.06,
      frequencyFunction: (time: number) => 2800 - time * 500,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 35) * 0.35,
    });
    const overtone = synthTone({
      duration: 0.05,
      frequencyFunction: (time: number) => 5600 - time * 1000,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 40) * 0.18,
    });
    const metallic = synthNoise(0.04, (progress) => Math.exp(-progress * 45) * 0.2, { type: 'bandpass', frequency: 3200, qualityFactor: 0.9 });
    return mix(mainTone, overtone, metallic);
  },

  coin_clink_multiple: (): Float32Array => {
    const clink1 = synthTone({
      duration: 0.05,
      frequencyFunction: (time: number) => 2600 - time * 400,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 38) * 0.28,
    });
    const clink2 = synthTone({
      duration: 0.06,
      frequencyFunction: (time: number) => 2900 - time * 450,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 35) * 0.3,
    });
    const clink3 = synthTone({
      duration: 0.05,
      frequencyFunction: (time: number) => 2400 - time * 380,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 40) * 0.26,
    });
    const clink4 = synthTone({
      duration: 0.06,
      frequencyFunction: (time: number) => 2750 - time * 420,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 36) * 0.29,
    });
    const metallic = synthNoise(0.15, (progress) => Math.exp(-progress * 20) * 0.22, { type: 'bandpass', frequency: 3000, qualityFactor: 1.1 });

    return mix(
      clink1,
      offsetSamples(clink2, Math.ceil(SAMPLE_RATE * 0.025)),
      offsetSamples(clink3, Math.ceil(SAMPLE_RATE * 0.055)),
      offsetSamples(clink4, Math.ceil(SAMPLE_RATE * 0.09)),
      metallic,
    );
  },

  coin_drop_bounce: (): Float32Array => {
    const impact1 = synthTone({
      duration: 0.08,
      frequencyFunction: (time: number) => 2200 - time * 600,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 28) * 0.38,
    });
    const bounce1 = synthTone({
      duration: 0.06,
      frequencyFunction: (time: number) => 2400 - time * 500,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 32) * 0.28,
    });
    const bounce2 = synthTone({
      duration: 0.05,
      frequencyFunction: (time: number) => 2600 - time * 450,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 35) * 0.22,
    });
    const bounce3 = synthTone({
      duration: 0.04,
      frequencyFunction: (time: number) => 2700 - time * 400,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 38) * 0.16,
    });
    const metallic = synthNoise(0.25, (progress) => Math.exp(-progress * 15) * 0.18, { type: 'bandpass', frequency: 2800, qualityFactor: 1.3 });

    return mix(
      impact1,
      offsetSamples(bounce1, Math.ceil(SAMPLE_RATE * 0.1)),
      offsetSamples(bounce2, Math.ceil(SAMPLE_RATE * 0.17)),
      offsetSamples(bounce3, Math.ceil(SAMPLE_RATE * 0.23)),
      metallic,
    );
  },

  ui_blip_high: (): Float32Array =>
    synthTone({
      duration: 0.04,
      frequencyFunction: 1200,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 80) * 0.5,
    }),

  ui_blip_mid: (): Float32Array =>
    synthTone({
      duration: 0.045,
      frequencyFunction: 800,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 70) * 0.55,
    }),

  ui_blip_low: (): Float32Array =>
    synthTone({
      duration: 0.05,
      frequencyFunction: 400,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 60) * 0.6,
    }),

  ui_blip_chirp_up: (): Float32Array =>
    synthTone({
      duration: 0.06,
      frequencyFunction: (time: number) => 600 + time * 1200,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 50) * 0.52,
    }),

  ui_blip_chirp_down: (): Float32Array =>
    synthTone({
      duration: 0.06,
      frequencyFunction: (time: number) => 1200 - time * 800,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 50) * 0.52,
    }),

  ui_beep_short: (): Float32Array =>
    synthTone({
      duration: 0.025,
      frequencyFunction: 880,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 120) * 0.48,
    }),

  ui_beep_double: (): Float32Array => {
    const beep1 = synthTone({
      duration: 0.04,
      frequencyFunction: 1000,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 80) * 0.45,
    });
    const beep2 = synthTone({
      duration: 0.04,
      frequencyFunction: 1200,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 80) * 0.45,
    });
    return mix(beep1, offsetSamples(beep2, Math.ceil(SAMPLE_RATE * 0.06)));
  },

  ui_beep_triple: (): Float32Array => {
    const beep1 = synthTone({
      duration: 0.035,
      frequencyFunction: 800,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 90) * 0.42,
    });
    const beep2 = synthTone({
      duration: 0.035,
      frequencyFunction: 1000,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 90) * 0.42,
    });
    const beep3 = synthTone({
      duration: 0.035,
      frequencyFunction: 1200,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 90) * 0.42,
    });
    return mix(beep1, offsetSamples(beep2, Math.ceil(SAMPLE_RATE * 0.05)), offsetSamples(beep3, Math.ceil(SAMPLE_RATE * 0.1)));
  },

  ui_swipe_fast: (): Float32Array =>
    synthNoise(0.08, (progress) => Math.sin(progress * Math.PI) * 0.6, {
      type: 'bandpass',
      frequency: (time: number) => 800 + time * 2400,
      qualityFactor: 1.8,
    }),

  ui_swipe_slow: (): Float32Array =>
    synthNoise(0.15, (progress) => Math.sin(progress * Math.PI) * 0.5, {
      type: 'bandpass',
      frequency: (time: number) => 600 + time * 1200,
      qualityFactor: 2.0,
    }),

  ui_swipe_reverse: (): Float32Array =>
    synthNoise(0.1, (progress) => Math.sin(progress * Math.PI) * 0.55, {
      type: 'bandpass',
      frequency: (time: number) => 2200 - time * 1800,
      qualityFactor: 1.9,
    }),

  ui_whoosh_short: (): Float32Array =>
    synthNoise(0.12, (progress) => Math.sin(progress * Math.PI) * 0.48, {
      type: 'lowpass',
      frequency: (time: number) => 600 + time * 1800,
      qualityFactor: 1.5,
    }),

  ui_whoosh_long: (): Float32Array =>
    synthNoise(0.22, (progress) => Math.sin(progress * Math.PI) * 0.52, {
      type: 'lowpass',
      frequency: (time: number) => 500 + time * 2000,
      qualityFactor: 1.6,
    }),

  ui_swoosh_up: (): Float32Array => {
    const noise = synthNoise(0.18, (progress) => Math.min(progress * 3, 1) * Math.exp(-progress * 8) * 0.5, {
      type: 'bandpass',
      frequency: (time: number) => 400 + time * 3000,
      qualityFactor: 2.2,
    });
    const tone = synthTone({
      duration: 0.16,
      frequencyFunction: (time: number) => 200 + time * 1400,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.min(progress * 4, 1) * Math.exp(-progress * 10) * 0.25,
    });
    return mix(noise, tone);
  },

  ui_swoosh_down: (): Float32Array => {
    const noise = synthNoise(0.18, (progress) => Math.exp(-progress * 7) * 0.5, {
      type: 'bandpass',
      frequency: (time: number) => 2800 - time * 2400,
      qualityFactor: 2.2,
    });
    const tone = synthTone({
      duration: 0.16,
      frequencyFunction: (time: number) => 1400 - time * 1200,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 10) * 0.25,
    });
    return mix(noise, tone);
  },

  ui_pop_soft: (): Float32Array =>
    synthTone({
      duration: 0.08,
      frequencyFunction: (time: number) => 600 * Math.exp(-time * 15),
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 30) * 0.4,
    }),

  ui_pop_medium: (): Float32Array =>
    synthTone({
      duration: 0.06,
      frequencyFunction: (time: number) => 900 * Math.exp(-time * 20),
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 40) * 0.5,
    }),

  ui_pop_sharp: (): Float32Array =>
    synthTone({
      duration: 0.05,
      frequencyFunction: (time: number) => 1400 * Math.exp(-time * 25),
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 50) * 0.55,
    }),

  ui_plop: (): Float32Array => {
    const tone = synthTone({
      duration: 0.1,
      frequencyFunction: (time: number) => 350 - time * 200,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 20) * 0.5,
    });
    const noise = synthNoise(0.04, (progress) => Math.exp(-progress * 40) * 0.2, { type: 'lowpass', frequency: 600, qualityFactor: 1.0 });
    return mix(tone, noise);
  },

  ui_hover_enter: (): Float32Array =>
    synthTone({
      duration: 0.035,
      frequencyFunction: 1100,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.min(progress * 8, 1) * Math.exp(-progress * 60) * 0.3,
    }),

  ui_hover_exit: (): Float32Array =>
    synthTone({
      duration: 0.03,
      frequencyFunction: 900,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 80) * 0.25,
    }),

  ui_toggle_on: (): Float32Array => {
    const tone = synthTone({
      duration: 0.08,
      frequencyFunction: (time: number) => 600 + time * 800,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.min(progress * 5, 1) * Math.exp(-progress * 18) * 0.48,
    });
    const click = synthNoise(0.03, (progress) => Math.exp(-progress * 60) * 0.25, { type: 'bandpass', frequency: 2000, qualityFactor: 0.8 });
    return mix(tone, click);
  },

  ui_toggle_off: (): Float32Array => {
    const tone = synthTone({
      duration: 0.08,
      frequencyFunction: (time: number) => 1400 - time * 800,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 18) * 0.45,
    });
    const click = synthNoise(0.03, (progress) => Math.exp(-progress * 60) * 0.22, { type: 'bandpass', frequency: 1800, qualityFactor: 0.8 });
    return mix(tone, click);
  },

  ui_tab_switch: (): Float32Array => {
    const swipe = synthNoise(0.1, (progress) => Math.sin(progress * Math.PI) * 0.1, {
      type: 'bandpass',
      frequency: (time: number) => 1000 + time * 1600,
      qualityFactor: 1.7,
    });
    return mix(swipe);
  },

  ui_expand: (): Float32Array =>
    synthTone({
      duration: 0.15,
      frequencyFunction: (time: number) => 400 + time * 900,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.min(progress * 4, 1) * Math.exp(-progress * 12) * 0.38,
    }),

  ui_collapse: (): Float32Array =>
    synthTone({
      duration: 0.15,
      frequencyFunction: (time: number) => 210 + time * 900,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.min(progress * 4, 1) * Math.exp(-progress * 12) * 0.38,
    }),

  ui_scroll_tick: (): Float32Array =>
    synthNoise(0.015, (progress) => Math.exp(-progress * 100) * 0.28, { type: 'bandpass', frequency: 1600, qualityFactor: 0.5 }),

  ui_success_gentle: (): Float32Array => {
    const tone1 = synthTone({
      duration: 0.12,
      frequencyFunction: 523,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 15) * 0.35,
    });
    const tone2 = synthTone({
      duration: 0.15,
      frequencyFunction: 659,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 12) * 0.32,
    });
    return mix(tone1, offsetSamples(tone2, Math.ceil(SAMPLE_RATE * 0.05)));
  },

  ui_success_bright: (): Float32Array => {
    const tone1 = synthTone({
      duration: 0.1,
      frequencyFunction: 659,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 18) * 0.38,
    });
    const tone2 = synthTone({
      duration: 0.12,
      frequencyFunction: 880,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 15) * 0.35,
    });
    const tone3 = synthTone({
      duration: 0.14,
      frequencyFunction: 1047,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 12) * 0.32,
    });
    return mix(tone1, offsetSamples(tone2, Math.ceil(SAMPLE_RATE * 0.045)), offsetSamples(tone3, Math.ceil(SAMPLE_RATE * 0.09)));
  },

  ui_error_soft: (): Float32Array => {
    const tone1 = synthTone({
      duration: 0.1,
      frequencyFunction: 440,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 18) * 0.4,
    });
    const tone2 = synthTone({
      duration: 0.12,
      frequencyFunction: 370,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 15) * 0.38,
    });
    return mix(tone1, offsetSamples(tone2, Math.ceil(SAMPLE_RATE * 0.06)));
  },

  ui_error_buzz: (): Float32Array => {
    const buzz1 = synthTone({
      duration: 0.08,
      frequencyFunction: 330,
      type: 'square',
      envelopeFunction: (progress: number) => Math.exp(-progress * 22) * 0.35,
    });
    const buzz2 = synthTone({
      duration: 0.08,
      frequencyFunction: 330,
      type: 'square',
      envelopeFunction: (progress: number) => Math.exp(-progress * 22) * 0.35,
    });
    return mix(buzz1, offsetSamples(buzz2, Math.ceil(SAMPLE_RATE * 0.12)));
  },

  ui_warning: (): Float32Array => {
    const tone = synthTone({
      duration: 0.15,
      frequencyFunction: (time: number) => 550 + Math.sin(time * 60) * 100,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 12) * 0.42,
    });
    const noise = synthNoise(0.12, (progress) => Math.exp(-progress * 15) * 0.2, { type: 'bandpass', frequency: 1800, qualityFactor: 1.5 });
    return mix(tone, noise);
  },

  ui_ding: (): Float32Array => {
    const main = synthTone({
      duration: 0.25,
      frequencyFunction: 1760,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 8) * 0.4,
    });
    const harmonic = synthTone({
      duration: 0.2,
      frequencyFunction: 3520,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 10) * 0.18,
    });
    return mix(main, harmonic);
  },

  ui_sparkle: (): Float32Array => {
    const high = synthTone({
      duration: 0.18,
      frequencyFunction: 2637,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 12) * 0.28,
    });
    const shimmer = synthNoise(0.15, (progress) => Math.exp(-progress * 14) * 0.12, { type: 'bandpass', frequency: 4000, qualityFactor: 3.0 });
    return mix(high, shimmer);
  },

  ui_tap_light: (): Float32Array =>
    synthNoise(0.025, (progress) => Math.exp(-progress * 90) * 0.35, { type: 'bandpass', frequency: 1400, qualityFactor: 0.7 }),

  ui_tap_heavy: (): Float32Array =>
    synthNoise(0.045, (progress) => Math.exp(-progress * 50) * 0.5, { type: 'bandpass', frequency: 1000, qualityFactor: 0.9 }),

  ui_ripple: (): Float32Array =>
    synthTone({
      duration: 0.3,
      frequencyFunction: (time: number) => 880 * Math.exp(-time * 6),
      type: 'sine',
      envelopeFunction: (progress: number) => Math.sin(progress * Math.PI) * Math.exp(-progress * 5) * 0.32,
    }),

  ui_shimmer: (): Float32Array => {
    const tone1 = synthTone({
      duration: 0.2,
      frequencyFunction: (time: number) => 1320 + Math.sin(time * 40) * 120,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 10) * 0.25,
    });
    const tone2 = synthTone({
      duration: 0.22,
      frequencyFunction: (time: number) => 1976 + Math.sin(time * 35) * 100,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 9) * 0.18,
    });
    const noise = synthNoise(0.25, (progress) => Math.exp(-progress * 8) * 0.1, { type: 'bandpass', frequency: 3200, qualityFactor: 2.8 });
    return mix(tone1, tone2, noise);
  },

  ui_tick_short: (): Float32Array =>
    synthNoise(0.018, (progress) => Math.exp(-progress * 110) * 0.32, { type: 'bandpass', frequency: 2600, qualityFactor: 0.4 }),

  ui_tick_long: (): Float32Array =>
    synthNoise(0.04, (progress) => Math.exp(-progress * 45) * 0.38, { type: 'bandpass', frequency: 2200, qualityFactor: 0.6 }),

  ui_plink: (): Float32Array =>
    synthTone({
      duration: 0.08,
      frequencyFunction: (time: number) => 2200 * Math.exp(-time * 18),
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 28) * 0.36,
    }),

  ui_bonk: (): Float32Array => {
    const tone = synthTone({
      duration: 0.06,
      frequencyFunction: (time: number) => 250 - time * 150,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 35) * 0.5,
    });
    const thud = synthNoise(0.04, (progress) => Math.exp(-progress * 50) * 0.3, { type: 'lowpass', frequency: 400, qualityFactor: 0.8 });
    return mix(tone, thud);
  },

  ui_zap: (): Float32Array =>
    synthNoise(0.05, (progress) => Math.exp(-progress * 60) * 0.45, {
      type: 'bandpass',
      frequency: (time: number) => 3000 - time * 1500,
      qualityFactor: 0.6,
    }),

  ui_ping: (): Float32Array => {
    const tone = synthTone({
      duration: 0.25,
      frequencyFunction: 1568,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 9) * 0.38,
    });
    const echo = synthTone({
      duration: 0.2,
      frequencyFunction: 1568,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 11) * 0.18,
    });
    return mix(tone, offsetSamples(echo, Math.ceil(SAMPLE_RATE * 0.1)));
  },

  footstep: (): Float32Array => {
    const impact = synthTone({
      duration: 0.08,
      frequencyFunction: (time: number) => 80 - time * 40,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 35) * 0.5,
    });
    const texture = synthNoise(0.12, (progress) => Math.exp(-progress * 25) * 0.35, { type: 'lowpass', frequency: 800, qualityFactor: 0.8 });
    const scrape = synthNoise(0.06, (progress) => Math.exp(-progress * 40) * 0.18, { type: 'bandpass', frequency: 1200, qualityFactor: 1.2 });
    return mix(impact, texture, offsetSamples(scrape, Math.ceil(SAMPLE_RATE * 0.02)));
  },

  footstep_heavy: (): Float32Array => {
    const impact = synthTone({
      duration: 0.12,
      frequencyFunction: (time: number) => 60 - time * 35,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 25) * 0.65,
    });
    const rumble = synthNoise(0.18, (progress) => Math.exp(-progress * 15) * 0.45, { type: 'lowpass', frequency: 400, qualityFactor: 1.2 });
    const thud = synthNoise(0.08, (progress) => Math.exp(-progress * 30) * 0.3, { type: 'lowpass', frequency: 600, qualityFactor: 0.9 });
    return mix(impact, rumble, thud);
  },

  footstep_light: (): Float32Array => {
    const impact = synthTone({
      duration: 0.06,
      frequencyFunction: (time: number) => 120 - time * 60,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 45) * 0.35,
    });
    const texture = synthNoise(0.08, (progress) => Math.exp(-progress * 35) * 0.22, { type: 'lowpass', frequency: 1000, qualityFactor: 0.9 });
    const soft = synthNoise(0.05, (progress) => Math.exp(-progress * 50) * 0.15, { type: 'bandpass', frequency: 1400, qualityFactor: 1.5 });
    return mix(impact, texture, soft);
  },

  footstep_wood: (): Float32Array => {
    const impact = synthTone({
      duration: 0.09,
      frequencyFunction: (time: number) => 140 - time * 70,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 30) * 0.48,
    });
    const creak = synthTone({
      duration: 0.15,
      frequencyFunction: (time: number) => 280 + Math.sin(time * 100) * 50,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 18) * 0.28,
    });
    const texture = synthNoise(0.1, (progress) => Math.exp(-progress * 28) * 0.25, { type: 'bandpass', frequency: 900, qualityFactor: 1.4 });
    return mix(impact, creak, texture);
  },

  footstep_stone: (): Float32Array => {
    const impact = synthTone({
      duration: 0.07,
      frequencyFunction: (time: number) => 100 - time * 50,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 40) * 0.52,
    });
    const click = synthNoise(0.04, (progress) => Math.exp(-progress * 55) * 0.38, { type: 'bandpass', frequency: 2200, qualityFactor: 0.7 });
    const echo = synthTone({
      duration: 0.12,
      frequencyFunction: 180,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 22) * 0.15,
    });
    const scrape = synthNoise(0.08, (progress) => Math.exp(-progress * 32) * 0.2, { type: 'bandpass', frequency: 1600, qualityFactor: 1.1 });
    return mix(impact, click, echo, scrape);
  },

  footstep_grass: (): Float32Array => {
    const impact = synthTone({
      duration: 0.1,
      frequencyFunction: (time: number) => 90 - time * 45,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 28) * 0.42,
    });
    const rustle = synthNoise(0.14, (progress) => Math.exp(-progress * 20) * 0.35, { type: 'bandpass', frequency: 1100, qualityFactor: 1.6 });
    const crunch = synthNoise(
      0.12,
      (progress) => {
        // Variable texture
        const noise = Math.sin(progress * 200) * 0.3 + 0.7;
        return Math.exp(-progress * 22) * 0.25 * noise;
      },
      { type: 'bandpass', frequency: 1400, qualityFactor: 1.3 },
    );
    return mix(impact, rustle, crunch);
  },

  // Running footstep — faster, lighter impact
  footstep_run: (): Float32Array => {
    const impact = synthTone({
      duration: 0.05,
      frequencyFunction: (time: number) => 110 - time * 60,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 55) * 0.42,
    });
    const quick = synthNoise(0.06, (progress) => Math.exp(-progress * 45) * 0.28, { type: 'lowpass', frequency: 900, qualityFactor: 0.7 });
    return mix(impact, quick);
  },

  // Sneaking footstep — very quiet, minimal
  footstep_sneak: (): Float32Array => {
    const impact = synthTone({
      duration: 0.05,
      frequencyFunction: (time: number) => 150 - time * 80,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 50) * 0.25,
    });
    const whisper = synthNoise(0.07, (progress) => Math.exp(-progress * 40) * 0.15, { type: 'lowpass', frequency: 1200, qualityFactor: 1.0 });
    return mix(impact, whisper);
  },

  footstep_metal: (): Float32Array => {
    const impact = synthTone({
      duration: 0.08,
      frequencyFunction: (time: number) => 180 - time * 90,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 32) * 0.5,
    });
    const clang = synthTone({
      duration: 0.12,
      frequencyFunction: (time: number) => 2400 - time * 600,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 25) * 0.28,
    });
    const ring = synthTone({
      duration: 0.15,
      frequencyFunction: 1800,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 18) * 0.12,
    });
    const metallic = synthNoise(0.1, (progress) => Math.exp(-progress * 28) * 0.22, { type: 'bandpass', frequency: 2800, qualityFactor: 1.0 });
    return mix(impact, clang, ring, metallic);
  },

  footstep_gravel: (): Float32Array => {
    const impact = synthTone({
      duration: 0.09,
      frequencyFunction: (time: number) => 95 - time * 50,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 30) * 0.46,
    });
    const crunch = synthNoise(
      0.16,
      (progress) => {
        // Lots of irregular texture
        const noise = Math.sin(progress * 300) * 0.4 + 0.6;
        return Math.exp(-progress * 18) * 0.38 * noise;
      },
      { type: 'bandpass', frequency: 1300, qualityFactor: 1.1 },
    );
    const scatter = synthNoise(0.12, (progress) => Math.exp(-progress * 22) * 0.2, { type: 'bandpass', frequency: 1800, qualityFactor: 0.8 });
    return mix(impact, crunch, scatter);
  },

  footstep_sand: (): Float32Array => {
    const impact = synthTone({
      duration: 0.11,
      frequencyFunction: (time: number) => 75 - time * 40,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 25) * 0.38,
    });
    const shift = synthNoise(
      0.15,
      (progress) => {
        // Smooth shifting texture
        const noise = Math.sin(progress * 80) * 0.3 + 0.7;
        return Math.exp(-progress * 20) * 0.32 * noise;
      },
      { type: 'lowpass', frequency: 700, qualityFactor: 1.4 },
    );
    return mix(impact, shift);
  },

  footstep_snow: (): Float32Array => {
    const impact = synthTone({
      duration: 0.1,
      frequencyFunction: (time: number) => 85 - time * 45,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 28) * 0.4,
    });
    const crunch = synthNoise(
      0.14,
      (progress) => {
        // Crispy texture
        const noise = Math.sin(progress * 180) * 0.35 + 0.65;
        return Math.exp(-progress * 22) * 0.36 * noise;
      },
      { type: 'bandpass', frequency: 1600, qualityFactor: 1.2 },
    );
    const compactNoise = synthNoise(0.08, (progress) => Math.exp(-progress * 32) * 0.22, { type: 'lowpass', frequency: 900, qualityFactor: 1.0 });
    return mix(impact, crunch, compactNoise);
  },

  footstep_splash: (): Float32Array => {
    const impact = synthTone({
      duration: 0.09,
      frequencyFunction: (time: number) => 100 - time * 50,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 30) * 0.44,
    });
    const splash = synthNoise(
      0.18,
      (progress) => {
        // Rising then falling
        if (progress < 0.2) {
          return (progress / 0.2) * Math.exp(-progress * 12) * 0.4;
        }
        return Math.exp(-progress * 15) * 0.4;
      },
      { type: 'bandpass', frequency: 1400, qualityFactor: 1.5 },
    );
    const water = synthTone({
      duration: 0.15,
      frequencyFunction: (time: number) => 300 + Math.sin(time * 60) * 80,
      type: 'sine',
      envelopeFunction: (progress: number) => Math.exp(-progress * 18) * 0.18,
    });
    return mix(impact, splash, water);
  },
};

let sharedAudioContext: AudioContext | null = null;

function getAudioContext(): AudioContext {
  if (!sharedAudioContext || sharedAudioContext.state === 'closed') {
    sharedAudioContext = new AudioContext();
  }
  if (sharedAudioContext.state === 'suspended') {
    sharedAudioContext.resume().catch(() => {
      // Resume can fail due to autoplay policy when triggered outside a user gesture
    });
  }
  return sharedAudioContext;
}

export function playGeneratedSfx(samples: Float32Array, volume: number): void {
  const audioContext = getAudioContext();
  const audioBuffer = audioContext.createBuffer(1, samples.length, SAMPLE_RATE);
  audioBuffer.getChannelData(0).set(samples);

  const source = audioContext.createBufferSource();
  const gainNode = audioContext.createGain();
  gainNode.gain.value = volume;

  source.buffer = audioBuffer;
  source.connect(gainNode);
  gainNode.connect(audioContext.destination);
  source.start();
}
